import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  IconButton,
  Grid,
  Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const CATEGORY_DATA = {
  Electronics: ["Mobile", "Laptop", "Headphones"],
  Clothing: ["Men", "Women", "Kids"],
  Furniture: ["Sofa", "Chair", "Table"]
};

export default function ProductForm() {
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [technical, setTechnical] = useState([{ key: "", value: "" }]);
  const [variants, setVariants] = useState([{ size: "", color: "", model: "" }]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);

  // Reset subcategory if category changes
  useEffect(() => setSubcategory(""), [category]);

  const handleTechChange = (i, field, value) => {
    const copy = [...technical];
    copy[i][field] = value;
    setTechnical(copy);
  };

  const addTech = () => setTechnical([...technical, { key: "", value: "" }]);
  const removeTech = (i) => setTechnical(technical.filter((_, x) => x !== i));

  const handleVariantChange = (i, field, value) => {
    const copy = [...variants];
    copy[i][field] = value;
    setVariants(copy);
  };

  const addVariant = () => setVariants([...variants, { size: "", color: "", model: "" }]);
  const removeVariant = (i) => setVariants(variants.filter((_, x) => x !== i));

  const handleDrop = (e, type) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (type === "image") setImages([...images, ...files.filter(f => f.type.startsWith("image/"))]);
    else setVideos([...videos, ...files.filter(f => f.type.startsWith("video/"))]);
  };

  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === "image") setImages([...images, ...files.filter(f => f.type.startsWith("image/"))]);
    else setVideos([...videos, ...files.filter(f => f.type.startsWith("video/"))]);
  };

  const removeImage = (i) => setImages(images.filter((_, x) => x !== i));
  const removeVideo = (i) => setVideos(videos.filter((_, x) => x !== i));

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("category", category);
    formData.append("subcategory", subcategory);
    formData.append("title", title);
    formData.append("desc", desc);
    formData.append("price", price);
    formData.append("technical", JSON.stringify(technical));
    formData.append("variants", JSON.stringify(variants));
    images.forEach(img => formData.append("images", img));
    videos.forEach(vid => formData.append("videos", vid));

    // console.log("Form ready to submit:", formData);
    alert("Product ready (check console)");
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" mb={3}>Add Product</Typography>

      {/* Category */}
      <TextField
        id="cat"
        select
        fullWidth
        label="Category"
        value={category}
        onChange={e => setCategory(e.target.value)}
        margin="normal"
      >
        {Object.keys(CATEGORY_DATA).map(cat => (
          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
        ))}
      </TextField>

      {/* Subcategory */}
      <TextField
        id="subcat"
        select
        fullWidth
        label="Subcategory"
        value={subcategory}
        disabled={!category}
        onChange={e => setSubcategory(e.target.value)}
        margin="normal"
      >
        {category && CATEGORY_DATA[category].map(sub => (
          <MenuItem key={sub} value={sub}>{sub}</MenuItem>
        ))}
      </TextField>

      {/* Title & Description */}
      <TextField
        fullWidth
        label="Title"
        margin="normal"
        value={title}
        onChange={e => setTitle(e.target.value)}
      />
      <TextField
        fullWidth
        label="Description"
        margin="normal"
        multiline
        rows={4}
        value={desc}
        onChange={e => setDesc(e.target.value)}
      />

      {/* Price */}
      <TextField
        fullWidth
        label="Price"
        type="number"
        margin="normal"
        value={price}
        onChange={e => setPrice(e.target.value)}
      />

      {/* Technical Details */}
      <Typography variant="h6" mt={3}>Technical Details</Typography>
      {technical.map((t, i) => (
        <Grid container spacing={2} key={i} mt={1}>
          <Grid item xs={5}>
            <TextField fullWidth label="Key" value={t.key} onChange={e => handleTechChange(i, "key", e.target.value)} />
          </Grid>
          <Grid item xs={5}>
            <TextField fullWidth label="Value" value={t.value} onChange={e => handleTechChange(i, "value", e.target.value)} />
          </Grid>
          <Grid item xs={2}>
            <IconButton color="error" onClick={() => removeTech(i)}><DeleteIcon /></IconButton>
          </Grid>
        </Grid>
      ))}
      <Button startIcon={<AddIcon />} onClick={addTech} sx={{ mt: 1 }}>Add Technical</Button>

      {/* Variants */}
      <Typography variant="h6" mt={4}>Variants</Typography>
      {variants.map((v, i) => (
        <Grid container spacing={2} key={i} mt={1}>
          <Grid item xs={4}>
            <TextField fullWidth label="Size" value={v.size} onChange={e => handleVariantChange(i, "size", e.target.value)} />
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Color" value={v.color} onChange={e => handleVariantChange(i, "color", e.target.value)} />
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth label="Model" value={v.model} onChange={e => handleVariantChange(i, "model", e.target.value)} />
          </Grid>
          <Grid item xs={1}>
            <IconButton color="error" onClick={() => removeVariant(i)}><DeleteIcon /></IconButton>
          </Grid>
        </Grid>
      ))}
      <Button startIcon={<AddIcon />} onClick={addVariant} sx={{ mt: 1 }}>Add Variant</Button>

      {/* Images */}
      <Typography variant="h6" mt={4}>Images</Typography>
      <Paper
        sx={{ p: 3, border: "2px dashed #888", textAlign: "center", cursor: "pointer" }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => handleDrop(e, "image")}
      >
        <Typography>Drag & Drop Images Here</Typography>
        <input type="file" multiple accept="image/*" onChange={e => handleFileSelect(e, "image")} style={{ display: "none" }} id="imgInput" />
      </Paper>
      <Grid container spacing={2} mt={1}>
        {images.map((img, i) => (
          <Grid item key={i}>
            <Box sx={{ position: "relative" }}>
              <img src={URL.createObjectURL(img)} alt="preview" width={120} height={120} style={{ objectFit: "cover" }} />
              <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0, bgcolor: "white" }} onClick={() => removeImage(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Videos */}
      <Typography variant="h6" mt={4}>Videos</Typography>
      <Paper
        sx={{ p: 3, border: "2px dashed #888", textAlign: "center", cursor: "pointer" }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => handleDrop(e, "video")}
      >
        <Typography>Drag & Drop Videos Here</Typography>
        <input type="file" multiple accept="video/*" onChange={e => handleFileSelect(e, "video")} style={{ display: "none" }} id="vidInput" />
      </Paper>
      <Grid container spacing={2} mt={1}>
        {videos.map((vid, i) => (
          <Grid item key={i}>
            <Box sx={{ position: "relative" }}>
              <video width={150} controls>
                <source src={URL.createObjectURL(vid)} />
              </video>
              <IconButton size="small" sx={{ position: "absolute", top: 0, right: 0, bgcolor: "white" }} onClick={() => removeVideo(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Button variant="contained" sx={{ mt: 4 }} onClick={handleSubmit}>Submit Product</Button>
    </Box>
  );
}
